// Single source of truth for QuotationEditor hotkeys.
//
// Every shortcut in the editor (and, in follow-up REQ-360 tasks, the right sidebar and the
// remaining actions) is declared here. The hotkey help panel (REQ-359) renders directly from
// EDITOR_HOTKEYS, and useQuotationViewer binds them through createEditorHotkeys + tinykeys.
//
// Combos are tinykeys strings. Where a binding must survive keyboard layouts and the macOS
// Option-key producing a different printable character (⌥1 → "¡"), the combo uses the physical
// `event.code` form (e.g. "Alt+Digit1") rather than the printable `event.key`.
//
// NOTE on the modifier: REQ-360 specs mod+1 (Ctrl on Win/Linux, Cmd on Mac), but Ctrl/Cmd+1 is
// reserved by Chrome/Edge/Firefox for tab-switching and cannot be intercepted by a web page.
// We ship Alt+1 (≡ Option+1 on macOS) as the interceptable default. The collision is escalated
// to design; the follow-up right-sidebar task must mirror this as Alt+3. Combos are declared
// here precisely so a design-approved remap is a one-line change.

export type THotkeyScope = "editor" | "leftSidebar" | "rightSidebar" | "headerMenu"

// Which tab of the keyboard-shortcuts help panel (REQ-359) a shortcut is listed under. Separate
// from `scope`, which says where a shortcut is ACTIVE: Alt+1 is bound editor-wide but reads as
// "General" to a user, and Alt+ArrowDown is right-sidebar-scoped AND listed under "Right sidebar
// navigation" — the two coincide there, but nothing makes them. The field is required on IHotkeyMeta, so a new shortcut cannot compile
// without saying where the panel shows it — which is REQ-359's "the list must reflect the hotkeys
// actually supported" turned into a type error rather than a convention.
export type THotkeyCategory = "general" | "rightSidebarNavigation" | "tools" | "objects" | "menus"

// The panel's tabs, in the order the design lists them. A category with no shortcuts is not
// rendered, so an entry can be added here ahead of the shortcuts that will fill it.
export const HOTKEY_CATEGORIES: { id: THotkeyCategory; label: string }[] = [
	{ id: "general", label: "General" },
	{ id: "rightSidebarNavigation", label: "Right sidebar navigation" },
	{ id: "tools", label: "Tools" },
	{ id: "objects", label: "Objects" },
	{ id: "menus", label: "Menus" },
]

export interface IHotkeyMeta {
	// Stable identifier — used to attach handlers and (later) to look up help-panel copy.
	id: string
	// tinykeys combo string, e.g. "Alt+Digit1", "Tab", "Shift+Tab", "Enter".
	combo: string
	// Extra combos bound to the SAME handler (e.g. Delete alongside Backspace). One shortcut on two
	// physical keys, so it stays a single entry and `label` names both.
	aliasCombos?: string[]
	// Human-readable combo for the help panel, e.g. "Alt + 1".
	label: string
	// What the shortcut does, for the help panel. Kept terse — the panel gives it one line of a
	// four-column grid (see KeyboardShortcutsPanel), so a sentence ellipses.
	description: string
	// Which context the shortcut belongs to.
	scope: THotkeyScope
	// Which tab of the help panel lists it (REQ-359).
	category: THotkeyCategory
	// Handled OUTSIDE this registry, by a keydown listener the orchestrator has owned since REQ-368,
	// which works on Windows, Linux and macOS. Such an entry is declared here purely so the help panel
	// can list it: the panel renders from EDITOR_HOTKEYS, while createEditorHotkeys skips any entry
	// with no handler — so declaring one costs nothing at runtime and binds nothing.
	//
	// DO NOT add an `on*` handler for these ids without deleting that listener in the SAME change.
	// Both would fire, and one Ctrl+V would paste two objects. (Measured: an earlier attempt to move
	// them in here did exactly that, because tinykeys resolves `$mod` to Control off a Mac, so
	// `$mod+KeyC` and a `Control+KeyC` alias were one keystroke with two bindings.)
	boundOutsideRegistry?: true
	// Apryse binds this same bare key to one of ITS OWN tools (UI.hotkeys.Keys → the arrow, highlight
	// and signature tools), and its listener runs before ours, so the key would do both things: open
	// our menu AND arm an Apryse tool. Set this and the dispatcher binds the shortcut in the CAPTURE
	// phase instead, stopping the event before Apryse ever sees it. Neither `UI.hotkeys.off(key)` nor
	// `UI.disableTools([tool])` prevents it — both were tried; see useEditorHotkeys.
	beatsApryseTool?: true
}

export interface IHotkey extends IHotkeyMeta {
	handler: (event: KeyboardEvent) => void
}

export const HOTKEY_IDS = {
	focusLeftSidebar: "focus-left-sidebar",
	cycleFocusForward: "cycle-focus-forward",
	cycleFocusBackward: "cycle-focus-backward",
	activateItem: "activate-item",
	navigateNextEref: "navigate-next-eref",
	navigatePrevEref: "navigate-prev-eref",
	navigateFurnitureToEref: "navigate-furniture-to-eref",
	focusRightSidebar: "focus-right-sidebar",
	openAddMenu: "open-add-menu",
	openHelpMenu: "open-help-menu",
	openShapesMenu: "open-shapes-menu",
	toggleSplitView: "toggle-split-view",
	openSettings: "open-settings",
	pickMenuItem: "pick-menu-item",
	closeRightSidebar: "close-right-sidebar",
	deleteSelection: "delete-selection",
	copyFurniture: "copy-furniture",
	pasteFurniture: "paste-furniture",
	duplicateFurniture: "duplicate-furniture",
	selectTool: "select-tool",
	panTool: "pan-tool",
	zoomTool: "zoom-tool",
	rulerTool: "ruler-tool",
	textTool: "text-tool",
	freehandHighlightTool: "freehand-highlight-tool",
} as const

export const EDITOR_HOTKEYS: IHotkeyMeta[] = [
	{
		id: HOTKEY_IDS.focusLeftSidebar,
		combo: "Alt+Digit1",
		label: "Alt + 1",
		description: "Select left sidebar",
		scope: "editor",
		category: "general",
	},
	// Right-sidebar focus + close (REQ-359/REQ-360, this task). Alt+3 mirrors the left sidebar's
	// Alt+1 (spec is mod+3 / Cmd+3, shipped as the interceptable Alt+3 — Ctrl/Cmd+3 is reserved by
	// the browser for tab-switching; Alt+2 is left for the middle nav menu). Uses the physical
	// event.code form so macOS Option+3 (→ "£") still matches. Escape closes the right sidebar and
	// returns focus to the left sidebar; it is exempt from the ignore-while-typing rule so it works
	// from inside a sidebar text field (the field handles its own Escape first — see useEditorHotkeys).
	{
		id: HOTKEY_IDS.focusRightSidebar,
		combo: "Alt+Digit3",
		label: "Alt + 3",
		description: "Select right sidebar",
		scope: "rightSidebar",
		category: "general",
	},
	{
		id: HOTKEY_IDS.closeRightSidebar,
		combo: "Escape",
		label: "Esc",
		description: "Close right sidebar",
		scope: "rightSidebar",
		category: "general",
	},
	{
		id: HOTKEY_IDS.cycleFocusForward,
		combo: "Tab",
		label: "Tab",
		description: "Focus the next sidebar item",
		scope: "leftSidebar",
		category: "general",
	},
	{
		id: HOTKEY_IDS.cycleFocusBackward,
		combo: "Shift+Tab",
		label: "Shift + Tab",
		description: "Focus the previous sidebar item",
		scope: "leftSidebar",
		category: "general",
	},
	{
		id: HOTKEY_IDS.activateItem,
		combo: "Enter",
		label: "Enter",
		description: "Open the focused item",
		scope: "leftSidebar",
		category: "general",
	},
	// Right-sidebar navigation (REQ-360, tasks 2 & 3). The spec is mod+Arrow (Ctrl on Win/Linux,
	// Cmd on Mac); we ship Alt (≡ Option on macOS) to match the interceptable Alt+1 above. Arrow
	// keys aren't printable, so event.key stays "ArrowDown/Up/Left" under Alt on every layout — no
	// event.code form is needed here. Alt+ArrowLeft/Right is the browser Back/Forward shortcut, so
	// the handler always preventDefaults these while the editor owns them (see useEditorHotkeys).
	{
		id: HOTKEY_IDS.navigateNextEref,
		combo: "Alt+ArrowDown",
		label: "Alt + ↓",
		description: "Next elevation reference",
		scope: "rightSidebar",
		category: "rightSidebarNavigation",
	},
	{
		id: HOTKEY_IDS.navigatePrevEref,
		combo: "Alt+ArrowUp",
		label: "Alt + ↑",
		description: "Previous elevation reference",
		scope: "rightSidebar",
		category: "rightSidebarNavigation",
	},
	{
		id: HOTKEY_IDS.navigateFurnitureToEref,
		combo: "Alt+ArrowLeft",
		label: "Alt + ←",
		description: "Back to parent elevation reference",
		scope: "rightSidebar",
		category: "rightSidebarNavigation",
	},
	// Canvas delete (REQ-369 objects, REQ-376 elevation reference): opens the shared delete
	// confirmation for whatever the canvas selection resolves to. Backspace and Delete are the same
	// shortcut, so Delete rides along as an alias. No modifier, and inert while typing (the
	// dispatcher's default gate) so Backspace still edits text in the sidebars.
	{
		id: HOTKEY_IDS.deleteSelection,
		combo: "Backspace",
		aliasCombos: ["Delete"],
		label: "Delete / ⌫",
		description: "Delete the selection",
		scope: "editor",
		category: "objects",
	},
	// Furniture clipboard (REQ-368) — DISPLAY-ONLY entries, see `boundOutsideRegistry` above. The
	// working mechanism is the orchestrator's own listener and is deliberately left alone; these exist
	// so the shortcuts appear in the panel's Objects tab, which is where users look for them.
	//
	// Control and Meta are listed as the two modifiers that listener accepts (`ctrlKey || metaKey`),
	// which is also what lets the panel render ⌘C on a Mac and Ctrl+C everywhere else.
	{
		id: HOTKEY_IDS.copyFurniture,
		combo: "Control+KeyC",
		aliasCombos: ["Meta+KeyC"],
		boundOutsideRegistry: true,
		label: "Ctrl + C",
		description: "Copy the selected object",
		scope: "editor",
		category: "objects",
	},
	{
		id: HOTKEY_IDS.pasteFurniture,
		combo: "Control+KeyV",
		aliasCombos: ["Meta+KeyV"],
		boundOutsideRegistry: true,
		label: "Ctrl + V",
		description: "Paste at the cursor",
		scope: "editor",
		category: "objects",
	},
	{
		id: HOTKEY_IDS.duplicateFurniture,
		combo: "Control+KeyD",
		aliasCombos: ["Meta+KeyD"],
		boundOutsideRegistry: true,
		label: "Ctrl + D",
		description: "Duplicate the selected object in place",
		scope: "editor",
		category: "objects",
	},
	{
		id: HOTKEY_IDS.selectTool,
		combo: "KeyV",
		label: "V",
		description: "Select tool",
		scope: "editor",
		category: "tools",
	},
	{
		id: HOTKEY_IDS.panTool,
		combo: "Shift+KeyV",
		label: "Shift + V",
		description: "Pan / hand tool",
		scope: "editor",
		category: "tools",
	},
	{
		id: HOTKEY_IDS.zoomTool,
		combo: "KeyZ",
		aliasCombos: ["Shift+KeyZ"],
		label: "Z / Shift + Z",
		description: "Zoom tool (Shift zooms out)",
		scope: "editor",
		category: "tools",
	},
	{
		id: HOTKEY_IDS.rulerTool,
		combo: "KeyM",
		label: "M",
		description: "Measurement / ruler tool",
		scope: "editor",
		category: "tools",
	},
	// The Text (note) tool, REQ-395/396 — named after what the user sees in the toolbar, per PR #1386
	// review. `T` is one of Apryse's OWN reserved single-letter tool shortcuts (its FreeText tool, see
	// `UI.hotkeys.Keys`), so it needs `beatsApryseTool`: the capture-phase binding is what stops a
	// bare press ALSO arming Apryse's raw FreeText tool outside this app's own note flow.
	{
		id: HOTKEY_IDS.textTool,
		combo: "KeyT",
		label: "T",
		description: "Text / note tool",
		scope: "editor",
		category: "tools",
		beatsApryseTool: true,
	},
	// REQ-400 #1324, named "Pen" and bound to P (PR #1392 review). Both halves of that came from the
	// reviewer: the design's "Highlight (H)" could not have H (the Help menu owns it since REQ-359),
	// and the D this shipped on first is being claimed by Dual view in a PR already in review.
	//
	// Unlike D, P IS one of Apryse's own reserved single letters
	// (P A C E F I L N O R Q T S G H K U X — P is its pan tool), so this one DOES need
	// `beatsApryseTool`: without it a bare P would arm Apryse's tool as well as ours.
	{
		id: HOTKEY_IDS.freehandHighlightTool,
		combo: "KeyP",
		label: "P",
		description: "Pen tool",
		scope: "editor",
		category: "tools",
		beatsApryseTool: true,
	},
	// The header's dropdown menus and the settings modal, reachable without the mouse. A / H / S open
	// a menu exactly as clicking its button does (press again to dismiss); C opens quotation settings.
	// The digits then pick an entry of whichever menu is up — one shortcut on five physical keys, so
	// it is a single entry plus aliasCombos, and the per-item numbers are shown in the menu itself.
	//
	// #1383 review: the C shortcut and the header button now say "quotation configuration", which is
	// what the modal they open has always been titled. Only the user-facing copy moved — the ids
	// (`openSettings`), the props (`onSettings`) and QuotationSettingsModal keep their names.
	//
	// REQ-400 review (#1381): the shape tool no longer has its own bare R/O letters — Rectangle and
	// Ellipse are entries of the Shapes menu S opens here, picked the same 1/2 way every other menu's
	// entries are (motivation from the review: "other menus have 1, 2, 3… I expect us to have more
	// tools hence more hotkeys, and I'm not sure we will be able to have letter hotkeys for all of
	// them"). And since S moved from Settings to Shapes, Settings moved to C — both still collide
	// with one of Apryse's OWN reserved single-letter tool shortcuts (Signature / Crop, see
	// `UI.hotkeys.Keys`), so both still need `beatsApryseTool` (see that field's doc comment and
	// viewerModes.tsx's UI.hotkeys.off calls for these two keys).
	{
		id: HOTKEY_IDS.openAddMenu,
		combo: "KeyA",
		label: "A",
		description: "Open the Add menu",
		scope: "editor",
		category: "menus",
		beatsApryseTool: true,
	},
	{
		id: HOTKEY_IDS.openHelpMenu,
		combo: "KeyH",
		label: "H",
		description: "Open the help menu",
		scope: "editor",
		category: "menus",
		beatsApryseTool: true,
	},
	{
		id: HOTKEY_IDS.openShapesMenu,
		combo: "KeyS",
		label: "S",
		description: "Open the Shapes menu",
		scope: "editor",
		category: "menus",
		beatsApryseTool: true,
	},
	// #1383: the dual-screen control's shortcut. "Dual screen" is what this mode is called to the
	// user (review picked the name off the D key); "split screen" survives in the code and the store,
	// which #1311/REQ-399 named. Its click is asymmetric on purpose (#1311 D11) and this mirrors it
	// exactly — with ONE pane D turns dual screen on, with TWO it opens the dual-screen menu, whose
	// rows the digits below then pick. One key rather than a separate "enter" and "open
	// the menu" pair, because the control it stands for is itself one button with those two states,
	// and the description says both halves so the help panel doesn't imply it only ever splits.
	//
	// No `beatsApryseTool`: D is NOT one of Apryse's reserved single-letter tool keys (checked against
	// the shipped bundle — P A C E F I L N O R Q T S G H K U X), so nothing needs stopping in the
	// capture phase. And tinykeys matches modifiers EXACTLY, so this bare binding cannot fire on the
	// Ctrl/Cmd+D that duplicates an object (see duplicateFurniture above).
	{
		id: HOTKEY_IDS.toggleSplitView,
		combo: "KeyD",
		label: "D",
		// It opens the menu in EVERY state since #1430 — it no longer enters dual screen itself,
		// because the menu's first two rows are the choice between a second view in this window and
		// one in its own window, and a keypress cannot stand for both. The rows are then picked with
		// the digits, exactly as in the + Add / help / Shapes menus.
		description: "Open the dual view menu",
		scope: "editor",
		category: "menus",
	},
	{
		id: HOTKEY_IDS.openSettings,
		combo: "KeyC",
		label: "C",
		description: "Open quotation configuration",
		scope: "editor",
		category: "menus",
		beatsApryseTool: true,
	},
	{
		id: HOTKEY_IDS.pickMenuItem,
		combo: "Digit1",
		aliasCombos: ["Digit2", "Digit3", "Digit4", "Digit5", "Digit6"],
		// A RANGE, not six alternatives: spelled out, the six chips plus their separators take more
		// than the row's share of the panel grid and squeeze the description down to "Pick an i…".
		label: "1 – 6",
		description: "Pick an item of the open menu",
		scope: "headerMenu",
		category: "menus",
	},
]

export type THotkeyHandlers = Record<string, (event: KeyboardEvent) => void>

// Attach handlers (keyed by hotkey id) to the declared metadata. Entries without a handler
// are skipped, so callers can wire up a subset of scopes incrementally.
export const createEditorHotkeys = (handlers: THotkeyHandlers): IHotkey[] =>
	EDITOR_HOTKEYS.filter((meta) => typeof handlers[meta.id] === "function").map((meta) => ({
		...meta,
		handler: handlers[meta.id],
	}))
